import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const validateField = (name, value) => {
  let error = '';
  if (name === 'name') {
    if (!value.trim()) error = 'CMA Name is required';
    else if (value.length > 100) error = 'CMA Name cannot exceed 100 characters';
  }
  if (name === 'type') {
    if (!value.trim()) error = 'Type is required';
    else if (!['CMA', 'CHA', 'BOTH'].includes(value)) error = 'Invalid type';
  }
  if (name === 'contact_person') {
    if (!value.trim()) error = 'Contact Person is required';
    else if (value.length > 100) error = 'Contact Person cannot exceed 100 characters';
  }
  if (name === 'phone') {
    if (!value.trim()) error = 'Phone is required';
    else if (!/^\d{10}$/.test(value)) error = 'Phone must be exactly 10 digits';
  }
  if (name === 'email') {
    if (!value.trim()) error = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Enter a valid email address';
    else if (value.length > 120) error = 'Email cannot exceed 120 characters';
  }
  if (name === 'address') {
    if (!value.trim()) error = 'Address is required';
  }
  if (name === 'gst_no') {
    if (value && value.length > 30) error = 'GST No cannot exceed 30 characters';
  }
  if (name === 'state') {
    if (!value.trim()) error = 'State is required';
    else if (value.length > 100) error = 'State cannot exceed 100 characters';
  }
  if (name === 'state_code') {
    if (!value.trim()) error = 'State Code is required';
    else if (value.length > 10) error = 'State Code cannot exceed 10 characters';
  }
  if (name === 'account_no') {
    if (value && value.length > 50) error = 'Account No cannot exceed 50 characters';
  }
  if (name === 'ifsc_code') {
    if (value && value.length > 20) error = 'IFSC Code cannot exceed 20 characters';
  }
  if (name === 'bank_name') {
    if (value && value.length > 100) error = 'Bank Name cannot exceed 100 characters';
  }
  // bank_address: no length restriction
  return error;
};

const validateWarehouse = (warehouse, idx, isEditMode = false, originalWarehouses = []) => {
  const errors = [];
  
  // Check if warehouse field is read-only (has existing value from database)
  const isWarehouseReadOnly = (fieldName, warehouseIndex) => {
    // Only check if this is edit mode and the field had an original value
    if (!isEditMode) return false;
    
    // Get the original warehouse data
    if (!originalWarehouses || !originalWarehouses[warehouseIndex]) return false;
    
    // Get the original value from the database (stored separately)
    const originalValue = originalWarehouses[warehouseIndex][fieldName];
    return originalValue && originalValue.toString().trim() !== '';
  };
  
  if (!warehouse.warehouse_name.trim()) errors.push(`Warehouse #${idx + 1}: Name is required`);
  else if (warehouse.warehouse_name.length > 120) errors.push(`Warehouse #${idx + 1}: Name cannot exceed 120 characters`);
  
  if (!warehouse.type) errors.push(`Warehouse #${idx + 1}: Type is required`);
  
  if (!warehouse.location.trim()) errors.push(`Warehouse #${idx + 1}: Location is required`);
  else if (warehouse.location.length > 120) errors.push(`Warehouse #${idx + 1}: Location cannot exceed 120 characters`);
  
  // Only validate state if it's not read-only (has existing value)
  if (!isWarehouseReadOnly('state', idx)) {
    if (!warehouse.state.trim()) errors.push(`Warehouse #${idx + 1}: State is required`);
    else if (warehouse.state.length > 60) errors.push(`Warehouse #${idx + 1}: State cannot exceed 60 characters`);
  }
  
  // Validate state_code - it should be auto-filled when state is selected
  if (!warehouse.state_code.trim()) errors.push(`Warehouse #${idx + 1}: State Code is required`);
  else if (warehouse.state_code.length > 10) errors.push(`Warehouse #${idx + 1}: State Code cannot exceed 10 characters`);
  
  return errors;
};

const validateForm = (form, isEditMode = false, originalFormData = {}) => {
  const errors = {};
  errors.name = validateField('name', form.name);
  errors.type = validateField('type', form.type);
  errors.contact_person = validateField('contact_person', form.contact_person);
  errors.phone = validateField('phone', form.phone);
  errors.email = validateField('email', form.email);
  errors.address = validateField('address', form.address);
  errors.gst_no = validateField('gst_no', form.gst_no);
  errors.state = validateField('state', form.state);
  errors.state_code = validateField('state_code', form.state_code);
  errors.account_no = validateField('account_no', form.account_no);
  errors.ifsc_code = validateField('ifsc_code', form.ifsc_code);
  errors.bank_name = validateField('bank_name', form.bank_name);
  // bank_address: no length restriction
  // Warehouses
  let warehouseErrors = [];
  if (!form.warehouses || !Array.isArray(form.warehouses) || form.warehouses.length === 0) {
    warehouseErrors.push('At least one warehouse is required');
  } else {
    form.warehouses.forEach((wh, idx) => {
      warehouseErrors = warehouseErrors.concat(validateWarehouse(wh, idx, isEditMode, originalFormData.warehouses));
    });
  }
  errors.warehouses = warehouseErrors;
  // Remove empty errors
  Object.keys(errors).forEach((key) => {
    if (!errors[key] || (Array.isArray(errors[key]) && errors[key].length === 0)) {
      delete errors[key];
    }
  });
  return errors;
};

const CmsWarehouseForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [form, setForm] = useState({
    name: '',
    type: '', // <-- Add type field
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_no: '',
    state: '',
    state_code: '',
    account_no: '',
    ifsc_code: '',
    bank_name: '',
    bank_address: '',
    warehouses: [
      { warehouse_name: '', type: '', location: '', state: '', state_code: '' }
    ]
  });
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    type: '', // <-- Add type field
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    gst_no: '',
    state: '',
    state_code: '',
    account_no: '',
    ifsc_code: '',
    bank_name: '',
    bank_address: '',
    warehouses: [
      { warehouse_name: '', type: '', location: '', state: '', state_code: '' }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch CMA data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching CMA with ID:', id);
      const fetchCma = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/cma-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch CMA');
          }
          const result = await res.json();
          console.log('Fetched CMA data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            
            // Handle warehouses data
            let warehouses = [];
            if (result.data.warehouses && Array.isArray(result.data.warehouses)) {
              warehouses = result.data.warehouses.map(wh => ({
                warehouse_name: wh.warehouse_name ?? '',
                type: wh.type ?? '',
                location: wh.location ?? '',
                state: wh.state ?? '',
                state_code: wh.state_code ?? ''
              }));
            }
            
            // If no warehouses, add one empty warehouse
            if (warehouses.length === 0) {
              warehouses = [{ warehouse_name: '', type: '', location: '', state: '', state_code: '' }];
            }
            
            const newFormData = {
              name: result.data.name ?? '',
              type: result.data.type ?? '', // <-- Set type from fetched data
              contact_person: result.data.contact_person ?? '',
              phone: result.data.phone ?? '',
              email: result.data.email ?? '',
              address: result.data.address ?? '',
              gst_no: result.data.gst_no ?? '',
              state: result.data.state ?? '',
              state_code: result.data.state_code ?? '',
              account_no: result.data.account_no ?? '',
              ifsc_code: result.data.ifsc_code ?? '',
              bank_name: result.data.bank_name ?? '',
              bank_address: result.data.bank_address ?? '',
              warehouses: warehouses
            };
            console.log('Setting new form data with warehouses:', newFormData);
            setForm(newFormData);
            // Store original values for read-only check - ensure we have a deep copy
            const originalData = JSON.parse(JSON.stringify(newFormData));
            setOriginalFormData(originalData);
          } else {
            toast.error('CMA not found');
            navigate('/cma');
          }
        } catch (error) {
          console.error('Error fetching CMA:', error);
          toast.error('Failed to load CMA data');
          navigate('/cma');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchCma();
    }
  }, [id, isEditMode, navigate]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('FormData changed:', form);
  }, [form]);

  // Debug: Monitor originalFormData changes
  useEffect(() => {
    console.log('OriginalFormData changed:', originalFormData);
  }, [originalFormData]);

  // Helper function to check if field is read-only
  const isReadOnly = (fieldName) => {
    // Only check if this is edit mode and the field had an original value
    if (!isEditMode) return false;
    
    // Get the original value from the database (stored separately)
    const originalValue = originalFormData[fieldName];
    return originalValue && originalValue.toString().trim() !== '';
  };

  // Helper function to check if warehouse field is read-only
  const isWarehouseReadOnly = (fieldName, warehouseIndex) => {
    // Only check if this is edit mode and the field had an original value
    if (!isEditMode) return false;
    
    // Get the original warehouse data
    const originalWarehouses = originalFormData.warehouses;
    if (!originalWarehouses || !originalWarehouses[warehouseIndex]) return false;
    
    // Get the original value from the database (stored separately)
    const originalValue = originalWarehouses[warehouseIndex][fieldName];
    return originalValue && originalValue.toString().trim() !== '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    setForm({ ...form, [name]: value });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const handleStateChange = (e) => {
    const { name, value } = e.target;
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    const selectedState = stateList.find(s => s.name === value);
    setForm({
      ...form,
      state: selectedState ? selectedState.name : '',
      state_code: selectedState ? selectedState.code : ''
    });
  };

  const handleWarehouseChange = (idx, e) => {
    const { name, value } = e.target;
    
    // Don't allow changes to read-only warehouse fields
    if (isWarehouseReadOnly(name, idx)) {
      return;
    }
    
    if (name === 'state') {
      const selectedState = stateList.find(s => s.name === value);
      const newWarehouses = form.warehouses.map((w, i) =>
        i === idx ? { ...w, state: selectedState ? selectedState.name : '', state_code: selectedState ? selectedState.code : '' } : w
      );
      setForm({ ...form, warehouses: newWarehouses });
    } else {
      const newWarehouses = form.warehouses.map((w, i) =>
        i === idx ? { ...w, [name]: value } : w
      );
      setForm({ ...form, warehouses: newWarehouses });
    }
  };

  const handleWarehouseBlur = (idx, e) => {
    const { name, value } = e.target;
    let error = '';
    
    // Skip validation for read-only fields
    if (isWarehouseReadOnly(name, idx)) {
      return;
    }
    
    if (name === 'warehouse_name' && (!value.trim() || value.length > 120)) {
      error = !value.trim() ? `Warehouse #${idx + 1}: Name is required` : `Warehouse #${idx + 1}: Name cannot exceed 120 characters`;
    }
    if (name === 'type' && !value) {
      error = `Warehouse #${idx + 1}: Type is required`;
    }
    if (name === 'location' && (!value.trim() || value.length > 120)) {
      error = !value.trim() ? `Warehouse #${idx + 1}: Location is required` : `Warehouse #${idx + 1}: Location cannot exceed 120 characters`;
    }
    if (name === 'state' && (!value.trim() || value.length > 60)) {
      error = !value.trim() ? `Warehouse #${idx + 1}: State is required` : `Warehouse #${idx + 1}: State cannot exceed 60 characters`;
    }
    if (error) {
      toast.error(error);
    }
  };

  const handleAddWarehouse = () => {
    setForm({
      ...form,
      warehouses: [...form.warehouses, { warehouse_name: '', type: '', location: '', state: '', state_code: '' }]
    });
  };

  const handleRemoveWarehouse = (idx) => {
    setForm({
      ...form,
      warehouses: form.warehouses.filter((_, i) => i !== idx)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const errors = validateForm(form, isEditMode, originalFormData);
    if (Object.keys(errors).length > 0) {
      // Show all errors as toasts
      Object.values(errors).forEach((msg) => {
        if (Array.isArray(msg)) {
          msg.forEach((m) => m && toast.error(m));
        } else if (msg) {
          toast.error(msg);
        }
      });
      setLoading(false);
      return;
    }
    try {
      const { warehouses, ...cmaFields } = form;
      
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/cma-put/${id}`
        : `${API_BASE_URL}/api/masters/cma-post`;
      
      console.log('=== SUBMITTING CMA DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', { ...cmaFields, type: form.type, warehouses });
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cmaFields, type: form.type, warehouses }), // Ensure type is sent
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'CMA Updated Successfully!' : 'CMA and Warehouses created successfully!');
        if (!isEditMode) {
          setForm({
            name: '', type: '', contact_person: '', phone: '', email: '', address: '', gst_no: '', state: '', state_code: '', account_no: '', ifsc_code: '', bank_name: '', bank_address: '',
            warehouses: [{ warehouse_name: '', type: '', location: '', state: '', state_code: '' }]
          });
        } else {
          navigate('/cma');
        }
      } else {
        toast.error(data.message || 'Error occurred.');
      }
    } catch (err) {
      toast.error('Network error.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b pb-3 mb-5">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-medium text-gray-800">{isEditMode ? 'Edit CMA/CHA' : 'CMA/CHA & Warehouses Entry'}</h2>
              {isEditMode && (
                <div className="text-xs text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/cma')}
              className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-3 py-1 text-xs rounded  font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
            >
              <FaArrowLeft />
              Back to List
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
        {/* CMA Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">CMA Name</label>
            <input type="text" name="name" value={form.name} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required autoComplete="off" maxLength={100} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Type</label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none rounded-none ${isReadOnly('type') ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus:border-[#10a5e9]'}`}
              required
              disabled={isReadOnly('type')}
            >
              <option value="">Select Type</option>
              <option value="CMA">CMA</option>
              <option value="CHA">CHA</option>
              <option value="BOTH">BOTH</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Contact Person</label>
            <input type="text" name="contact_person" value={form.contact_person} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required autoComplete="off" maxLength={100} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Phone</label>
            <input type="text" name="phone" value={form.phone} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required autoComplete="off" maxLength={10} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Email</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required autoComplete="off" maxLength={120} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">GST No</label>
            <input type="text" name="gst_no" value={form.gst_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" autoComplete="off" maxLength={30} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">State</label>

            <select 
              name="state" 
              value={form.state} 
              onChange={handleStateChange} 
              onBlur={handleBlur} 
              className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none rounded-none ${isReadOnly('state') ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus:border-[#10a5e9]'}`}
              required
              disabled={isReadOnly('state')}
            >

              <option value="">Select State</option>
              {stateList.map((s) => (
                <option key={s.code} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">State Code</label>
            <input 
              type="text" 
              name="state_code" 
              value={form.state_code} 
              readOnly 
              className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none rounded-none ${isReadOnly('state_code') ? 'bg-gray-100' : 'bg-gray-100'}`}
              maxLength={10} 
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Account No</label>
            <input type="text" name="account_no" value={form.account_no} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" autoComplete="off" maxLength={50} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">IFSC Code</label>
            <input type="text" name="ifsc_code" value={form.ifsc_code} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" autoComplete="off" maxLength={20} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Bank Name</label>
            <input type="text" name="bank_name" value={form.bank_name} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" autoComplete="off" maxLength={100} />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Bank Address</label>
            <input type="text" name="bank_address" value={form.bank_address} onChange={handleChange} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" autoComplete="off" />
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-sm text-gray-700 mb-1">Address</label>
          <textarea name="address" value={form.address} onChange={handleChange} onBlur={handleBlur} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none resize-none" required rows={2} />
        </div>
        {/* Warehouses Table */}
        <div className="pt-2">
          <label className="block text-sm font-medium text-gray-800 mb-2">Warehouses</label>
          <div className="overflow-x-auto rounded-none border border-gray-300">
            <table className="min-w-full bg-white text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-1.5 border-b text-left font-medium">Name</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">Type</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">Location</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">State</th>
                  <th className="px-3 py-1.5 border-b text-left font-medium">State Code</th>
                  <th className="px-3 py-1.5 border-b"></th>
                </tr>
              </thead>
              <tbody>
                {form.warehouses.map((warehouse, idx) => (
                  <tr key={idx} className="border-b last:border-b-0">
                    <td className="px-3 py-1.5">
                      <input 
                        type="text" 
                        name="warehouse_name" 
                        placeholder="Warehouse Name" 
                        value={warehouse.warehouse_name} 
                        onChange={e => handleWarehouseChange(idx, e)} 
                        onBlur={e => handleWarehouseBlur(idx, e)} 
                        className={`w-full border border-gray-300 px-2 py-1 text-sm focus:outline-none rounded-none ${isWarehouseReadOnly('warehouse_name', idx) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus:border-[#10a5e9]'}`}
                        required={!isWarehouseReadOnly('warehouse_name', idx)}
                        disabled={isWarehouseReadOnly('warehouse_name', idx)}
                        autoComplete="off" 
                        maxLength={120} 
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <select 
                        name="type" 
                        value={warehouse.type} 
                        onChange={e => handleWarehouseChange(idx, e)} 
                        onBlur={e => handleWarehouseBlur(idx, e)} 
                        className={`w-full border border-gray-300 px-2 py-1 text-sm focus:outline-none rounded-none ${isWarehouseReadOnly('type', idx) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus:border-[#10a5e9]'}`}
                        required={!isWarehouseReadOnly('type', idx)}
                        disabled={isWarehouseReadOnly('type', idx)}
                      >
                        <option value="">Type</option>
                        <option value="PORT">PORT</option>
                        <option value="DOMESTIC">DOMESTIC</option>
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input 
                        type="text" 
                        name="location" 
                        placeholder="Location" 
                        value={warehouse.location} 
                        onChange={e => handleWarehouseChange(idx, e)} 
                        onBlur={e => handleWarehouseBlur(idx, e)} 
                        className={`w-full border border-gray-300 px-2 py-1 text-sm focus:outline-none rounded-none ${isWarehouseReadOnly('location', idx) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus:border-[#10a5e9]'}`}
                        required={!isWarehouseReadOnly('location', idx)}
                        disabled={isWarehouseReadOnly('location', idx)}
                        autoComplete="off" 
                        maxLength={120} 
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <select 
                        name="state" 
                        value={warehouse.state} 
                        onChange={e => handleWarehouseChange(idx, e)} 
                        onBlur={e => handleWarehouseBlur(idx, e)} 
                        className={`w-full border border-gray-300 px-2 py-1 text-sm focus:outline-none rounded-none ${isWarehouseReadOnly('state', idx) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white focus:border-[#10a5e9]'}`}
                        required={!isWarehouseReadOnly('state', idx)}
                        disabled={isWarehouseReadOnly('state', idx)}
                      >
                        <option value="">Select State</option>
                        {stateList.map((s) => (
                          <option key={s.code} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input type="text" name="state_code" value={warehouse.state_code} readOnly className="w-full border border-gray-300 px-2 py-1 text-sm bg-gray-100 focus:outline-none focus:border-[#10a5e9] rounded-none" maxLength={10} />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      {form.warehouses.length > 1 && (
                        <button type="button" onClick={() => handleRemoveWarehouse(idx)} className="text-sm px-3 py-1 border rounded text-[#10a5e9] border-[#10a5e9] hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9]">Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-2">
            <button type="button" onClick={handleAddWarehouse} className="text-sm px-3 py-1 rounded border font-medium bg-[#10a5e9] text-white border-[#10a5e9] hover:bg-[#0e8ac2]">+ Add Warehouse</button>
          </div>
        </div>
        <div className="pt-2 flex items-center gap-3">
          <button type="submit" className="px-4 py-2 text-sm bg-[#10a5e9] text-white font-normal rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60" style={{ minWidth: '120px' }} disabled={loading}>
            {loading ? 'Saving...' : (isEditMode ? 'Update CMA' : 'Save CMA & Warehouses')}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default CmsWarehouseForm;
